import React, { useState, useRef, useEffect } from 'react';

export type MenuOption = {
  key: string,
  label?: string;
  /**
   * Fundo do item. ⚠️ O item habilitado é sempre `text-white`, então SEM fundo ele fica branco no
   * branco — invisível até passar o mouse. Por isso existe o default abaixo: o tipo diz que a cor
   * é opcional, e agora ela é de verdade.
   */
  backgroundColor?: string;
  type: 'item' | 'separator';
  visible?: boolean | undefined;
  /** Item aparece mas não é clicável (ação ainda não disponível). */
  disabled?: boolean;
  /** Texto do tooltip — útil para explicar o porquê de um item desabilitado. */
  title?: string;
};

type MenuButtonProps = {
  options: MenuOption[];
  onSelect: (option: string) => void;
  /** Rótulo acessível do botão (default "Open menu"). */
  ariaLabel?: string;
};

/** slate-600 — neutro, e legível sob o `text-white` que todo item habilitado carrega. */
const DEFAULT_ITEM_BACKGROUND = '#475569';

const MenuButton: React.FC<MenuButtonProps> = ({ options, onSelect, ariaLabel = 'Open menu' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showAbove, setShowAbove] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuContentRef = useRef<HTMLDivElement>(null);
  
  const handleToggle = () => {
    // Calcular a posição antes de abrir o menu
    if (!isOpen) {
      setTimeout(() => calculatePosition(), 0);
    }
    setIsOpen(!isOpen);
  };

  options = options.map(option => ({ ...option, visible: option.visible ?? true }));

  const handleOptionClick = (event: React.MouseEvent, option: MenuOption) => {
    event.stopPropagation();
    if (option.disabled) return;   // mantém o menu aberto: o clique não fez nada
    onSelect(option.key);
    setIsOpen(false);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
      setIsOpen(false);
    }
  };

  const calculatePosition = () => {
    if (menuRef.current && menuContentRef.current) {
      const buttonRect = menuRef.current.getBoundingClientRect();
      const menuHeight = menuContentRef.current.offsetHeight;
      const windowHeight = window.innerHeight;
      
      // Verifica se há espaço suficiente abaixo do botão
      const spaceBelow = windowHeight - buttonRect.bottom;
      setShowAbove(spaceBelow < menuHeight && buttonRect.top > menuHeight);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', calculatePosition);
    window.addEventListener('scroll', calculatePosition);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', calculatePosition);
      window.removeEventListener('scroll', calculatePosition);
    };
  }, []);
  
  useEffect(() => {
    if (isOpen) {
      calculatePosition();
    }
  }, [isOpen]);

  return (
    <div className="relative inline-block" ref={menuRef}>
      <button
        onClick={handleToggle}
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
      >
        <span className="sr-only">{ariaLabel}</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M12 6v.01M12 12v.01M12 18v.01"
          />
        </svg>
      </button>

      {isOpen && (
        <div 
          ref={menuContentRef}
          role="menu"
          className={`absolute right-0 w-48 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-md shadow-lg z-10 ${
            showAbove ? 'bottom-full mb-2' : 'top-full mt-2'
          }`}
        >
          <ul className='p-2 bg-slate-100 dark:bg-slate-800 drop-shadow-md'>
            {
              options
                .filter(option => option.visible === true)
                .map((option, index) => (
                  option.type === 'separator' ? (
                    <hr key={`separator-${index}`} className="border-gray-300 dark:border-slate-600 my-2" />
                  ) : (
                    <li
                      key={option.key}
                      role="menuitem"
                      title={option.title}
                      aria-disabled={option.disabled || undefined}
                      className={`block px-4 py-2 mb-1 border dark:border-slate-600 rounded-md ${
                        option.disabled
                          ? 'cursor-not-allowed opacity-50 text-gray-500 dark:text-slate-400 bg-slate-200 dark:bg-slate-700'
                          : 'cursor-pointer text-white hover:bg-slate-600'
                      }`}
                      onClick={(event) => handleOptionClick(event, option)}
                      style={option.disabled ? undefined
                        : { backgroundColor: option.backgroundColor || DEFAULT_ITEM_BACKGROUND }}
                    >
                      {option.label}
                    </li>
                  )
                ))
            }
          </ul>
        </div>
      )}
    </div>
  );
};

export default MenuButton;